import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Calendar, 
  User, 
  Search,
  FileText,
  Settings,
  Save,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    cover_image: '',
    preview_content: '',
    full_content: '',
    author: '',
    is_active: true,
    is_featured: false,
    meta_title: '',
    meta_description: '',
    tags: ''
  });

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/blog/articles`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    try {
      const articleData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };
      
      const response = await axios.post(`${API}/admin/blog/articles`, articleData);
      
      setArticles(prev => [response.data, ...prev]);
      setShowCreateModal(false);
      resetForm();
      alert('Articolo creato con successo!');
    } catch (error) {
      console.error('Error creating article:', error);
      alert('Errore nella creazione dell\'articolo');
    }
  };

  const handleUpdateArticle = async (e) => {
    e.preventDefault();
    try {
      const articleData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };
      
      const response = await axios.put(`${API}/admin/blog/articles/${editingArticle.id}`, articleData);
      
      setArticles(prev => prev.map(article => 
        article.id === editingArticle.id ? response.data : article
      ));
      setEditingArticle(null);
      resetForm();
      alert('Articolo aggiornato con successo!');
    } catch (error) {
      console.error('Error updating article:', error);
      alert('Errore nell\'aggiornamento dell\'articolo');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo articolo?')) return;
    
    try {
      await axios.delete(`${API}/admin/blog/articles/${articleId}`);
      setArticles(prev => prev.filter(article => article.id !== articleId));
      alert('Articolo eliminato con successo!');
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Errore nell\'eliminazione dell\'articolo');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      cover_image: '',
      preview_content: '',
      full_content: '',
      author: '',
      is_active: true,
      is_featured: false,
      meta_title: '',
      meta_description: '',
      tags: ''
    });
  };

  const openEditModal = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      category: article.category,
      cover_image: article.cover_image,
      preview_content: article.preview_content,
      full_content: article.full_content,
      author: article.author,
      is_active: article.is_active,
      is_featured: article.is_featured,
      meta_title: article.meta_title || '',
      meta_description: article.meta_description || '',
      tags: article.tags.join(', ')
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2">
                <img 
                  src="/logo-originale.png" 
                  alt="InLeads Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="text-2xl font-bold text-gray-900">InLeads</span>
              </Link>
              <Badge variant="secondary" className="ml-4">
                <Settings className="h-3 w-3 mr-1" />
                Admin Panel
              </Badge>
            </div>
            
            <nav className="flex items-center space-x-4">
              <Link to="/" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Sito
              </Link>
              <Link to="/blog" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Blog
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestione Blog</h1>
            <p className="text-gray-600">Crea e gestisci gli articoli del blog InLeads</p>
          </div>

          {/* Filters and Actions */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex-1 flex gap-4 w-full md:w-auto">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Cerca articoli..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Tutte le categorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Tutte le categorie</SelectItem>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-[#0047FF] hover:bg-[#07396B]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Articolo
              </Button>
            </div>
          </div>

          {/* Articles Table */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0047FF] mx-auto"></div>
                <p className="mt-4 text-gray-600">Caricamento articoli...</p>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun articolo trovato</h3>
                <p className="text-gray-600">Inizia creando il tuo primo articolo del blog.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold text-gray-900">Titolo</th>
                      <th className="text-left p-4 font-semibold text-gray-900">Categoria</th>
                      <th className="text-left p-4 font-semibold text-gray-900">Autore</th>
                      <th className="text-left p-4 font-semibold text-gray-900">Data</th>
                      <th className="text-left p-4 font-semibold text-gray-900">Stato</th>
                      <th className="text-left p-4 font-semibold text-gray-900">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredArticles.map((article) => (
                      <tr key={article.id} className="border-b hover:bg-gray-50">
                        <td className="p-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={article.cover_image}
                              alt={article.title}
                              className="w-12 h-12 object-cover rounded"
                            />
                            <div>
                              <div className="font-medium text-gray-900 max-w-xs truncate">
                                {article.title}
                              </div>
                              <div className="text-sm text-gray-600">
                                {article.slug}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className="text-xs">
                            {article.category}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-1">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{article.author}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {formatDate(article.published_date)}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            {article.is_active ? (
                              <div className="flex items-center space-x-1">
                                <Eye className="h-4 w-4 text-green-600" />
                                <span className="text-sm text-green-600">Pubblicato</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1">
                                <EyeOff className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-400">Bozza</span>
                              </div>
                            )}
                            {article.is_featured && (
                              <Badge variant="secondary" className="text-xs">
                                In evidenza
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(article)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteArticle(article.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Article Modal */}
      <Dialog 
        open={showCreateModal || editingArticle !== null} 
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false);
            setEditingArticle(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Modifica Articolo' : 'Nuovo Articolo'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={editingArticle ? handleUpdateArticle : handleCreateArticle} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titolo *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Inserisci il titolo dell'articolo"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Categoria *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="author">Autore *</Label>
                <Input
                  id="author"
                  value={formData.author}
                  onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                  placeholder="Nome dell'autore"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cover_image">URL Immagine Copertina *</Label>
                <Input
                  id="cover_image"
                  value={formData.cover_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, cover_image: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preview_content">Contenuto Anteprima (Visibile pubblicamente) *</Label>
              <Textarea
                id="preview_content"
                value={formData.preview_content}
                onChange={(e) => setFormData(prev => ({ ...prev, preview_content: e.target.value }))}
                placeholder="Inserisci l'anteprima dell'articolo che sarà visibile pubblicamente..."
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="full_content">Contenuto Completo (Solo in app) *</Label>
              <Textarea
                id="full_content"
                value={formData.full_content}
                onChange={(e) => setFormData(prev => ({ ...prev, full_content: e.target.value }))}
                placeholder="Inserisci il contenuto completo dell'articolo che sarà visibile solo nell'app..."
                rows={6}
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="meta_title">Meta Title (SEO)</Label>
                <Input
                  id="meta_title"
                  value={formData.meta_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                  placeholder="Titolo SEO personalizzato"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (separati da virgola)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="immobiliare, vendita, agenti"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta_description">Meta Description (SEO)</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                placeholder="Descrizione SEO dell'articolo"
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Pubblica articolo</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                />
                <Label htmlFor="is_featured">In evidenza</Label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingArticle(null);
                  resetForm();
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Annulla
              </Button>
              <Button type="submit" className="bg-[#0047FF] hover:bg-[#07396B]">
                <Save className="h-4 w-4 mr-2" />
                {editingArticle ? 'Aggiorna' : 'Crea'} Articolo
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;